import { useMemo } from 'react';
import { normalizeRank } from '../taxonomy/normalize';
import { getRecordStatus } from '../taxonomy/status';
import { resolveFamilyName, resolveSubfamilyName } from '../taxonomy/resolve';

const determineContext = (extantFossil) => {
  // If empty or null, treat as neontological (extant)
  if (!extantFossil) return 'neontological';
  const val = extantFossil.toLowerCase();
  // If it contains 'fossil' (covers 'Fossil', 'extant_fossil', etc.), treat as paleontological
  if (val.includes('fossil')) return 'paleontological';
  // Everything else is treated as neontological
  return 'neontological';
};

export const useConceptRecords = ({
  rawData,
  recordMap,
  familyCache,
  subfamilyCache,
  conceptUnit,
}) => useMemo(() => {
  if (!rawData.length) return [];
  const conceptMap = new Map();

  rawData.forEach((record) => {
    const year = Number(record.authority_year);
    if (!Number.isFinite(year)) return;
    if (conceptUnit === 'acceptedOnly' && getRecordStatus(record) !== 'valid') return;

    const conceptId = conceptUnit === 'mergeSynonyms'
      ? (record.valid_name_id || record.id)
      : record.id;
    if (!conceptId) return;

    const conceptRecord = recordMap.get(conceptId) || record;
    const conceptRank = normalizeRank(conceptRecord.current_rank || record.current_rank);
    const conceptStatus = getRecordStatus(conceptRecord);
    
    // Use pre-resolved values from RPC if available, otherwise fallback to client-side resolution
    const family = record.family || resolveFamilyName(conceptRecord.id || record.id, recordMap, familyCache);
    const subfamily = record.subfamily || resolveSubfamilyName(conceptRecord.id || record.id, recordMap, subfamilyCache);
    
    const recordContext = determineContext(record.extant_fossil);
    const existing = conceptMap.get(conceptId);

    if (!existing) {
      conceptMap.set(conceptId, {
        id: conceptId,
        year,
        rank: conceptRank,
        status: conceptStatus,
        family,
        subfamily,
        contextSet: new Set([recordContext]),
        // Final context will be resolved after iteration
        context: recordContext,
      });
      return;
    }

    existing.contextSet.add(recordContext);
    
    const next = {
      ...existing,
      year: Math.min(existing.year, year),
      rank: existing.rank || conceptRank,
      status: existing.status || conceptStatus,
      family: existing.family || family,
      subfamily: existing.subfamily || subfamily,
      contextSet: existing.contextSet,
    };
    conceptMap.set(conceptId, next);
  });

  // Resolve final context for each concept
  return Array.from(conceptMap.values()).map(concept => {
    const { contextSet } = concept;
    let finalContext = 'unknown';

    const hasNeo = contextSet.has('neontological');
    const hasPaleo = contextSet.has('paleontological');
    const hasUnknown = contextSet.has('unknown');

    if (hasNeo && hasPaleo) {
      finalContext = 'mixed';
    } else if (hasNeo) {
      finalContext = 'neontological';
    } else if (hasPaleo) {
      finalContext = 'paleontological';
    } else if (hasUnknown) {
      finalContext = 'unknown';
    }

    return {
      ...concept,
      context: finalContext,
      // Remove temporary set
      contextSet: undefined,
    };
  });
}, [rawData, recordMap, familyCache, subfamilyCache, conceptUnit]);
